import React, { useState, useEffect, useRef } from 'react';
import {
    TextField,
    Tooltip,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Box,
    Avatar,
} from '@mui/material';
import Draggable from 'react-draggable';
import { styled } from '@mui/system';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { useTranslation } from 'react-i18next';
import ClientItemPricesEditor, { arePricesValid } from './ClientItemPricesEditor';
import { normalizePrices, getDefaultPriceAmount } from '../../../helpers/clientItemPrices';

const StyledContainer = styled('div')({
    position: 'absolute',
    top: '10%',
    left: '20%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    minHeight: '40vh',
    maxHeight: '75vh',
    overflowY: 'auto',
    minWidth: '500px',
    zIndex: 1300,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.4)',
    border: '1px solid #304fff',
});

function ClientItemCreateModal({ open, onClose, onSave, existingItems = [] }) {
    const { t, i18n } = useTranslation();
    const nodeRef = useRef(null);
    const [direction, setDirection] = useState('rtl');

    const [itemsToAdd, setItemsToAdd] = useState([]);

    useEffect(() => {
        setDirection(i18n.language === 'he' || i18n.language === 'ar' ? 'rtl' : 'ltr');
    }, [i18n.language]);


    useEffect(() => {
        if (existingItems) {
            let newMakatNumber = 1;
            if (existingItems.length > 0) {
                const maxMakat = Math.max(
                    ...existingItems.map((it) => parseInt(it.makat, 10) || 0)
                );
                newMakatNumber = maxMakat + 1;
            }
            setItemsToAdd([{ makat: newMakatNumber.toString(), name: '', price: '', prices: normalizePrices(null, ''), includeVat: true, imageFile: null, imagePreview: null }]);
        }
    }, [existingItems]);

    const inputPropsStyle = {
        disableUnderline: true,
        className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
        sx: {
            height: '48px',
            padding: '0 10px',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: '1px solid transparent',
            '&:focus-within': {
                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
            },
        },
    };

    const inputStyle = {
        style: {
            textAlign: direction === 'rtl' ? 'right' : 'left',
            direction: direction === 'rtl' ? 'rtl' : 'ltr',
        },
    };

    const handleAddRow = () => {
        setItemsToAdd((prev) => {
            let newMakat = '1';
            if (prev.length > 0) {
                const maxMakat = Math.max(
                    ...prev.map((it) => parseInt(it.makat, 10) || 0)
                );
                newMakat = `${maxMakat + 1}`;
            }
            return [...prev, { makat: newMakat, name: '', price: '', prices: normalizePrices(null, ''), includeVat: true, imageFile: null, imagePreview: null }];
        });
    };

    const handleRemoveRow = (index) => {
        setItemsToAdd((prev) => prev.filter((_, i) => i !== index));
    };

    const handleChange = (index, field, value) => {
        setItemsToAdd((prev) => {
            const updatedItems = [...prev];
            const row = { ...updatedItems[index], [field]: value };
            if (field === 'prices') {
                row.price = getDefaultPriceAmount(value, '') ?? '';
            }
            updatedItems[index] = row;
            return updatedItems;
        });
    };

    const handleImageChange = (index, event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setItemsToAdd((prev) => {
                    const updatedItems = [...prev];
                    updatedItems[index] = {
                        ...updatedItems[index],
                        imageFile: file,
                        imagePreview: reader.result
                    };
                    return updatedItems;
                });
            };
            reader.readAsDataURL(file);
        }
        event.target.value = '';
    };

    const handleRemoveImage = (index) => {
        setItemsToAdd((prev) => {
            const updatedItems = [...prev];
            const { imageFile, imagePreview, ...rest } = updatedItems[index];
            updatedItems[index] = { ...rest, imageFile: null, imagePreview: null };
            return updatedItems;
        });
    };

    const isFormValid = () => {
        return itemsToAdd.every(item => 
            item.makat && item.makat.trim() !== '' && 
            item.name && item.name.trim() !== '' && 
            arePricesValid(item.prices)
        );
    };

    const handleSave = () => {
        if (isFormValid()) {
            onSave(itemsToAdd);
        }
    };

    if (!open) return null;

    return (
        <Draggable  handle=".handle" nodeRef={nodeRef} >
            <StyledContainer ref={nodeRef} style={{ direction }}>
                {/* Header */}
                <div
                    className="handle"
                    style={{
                        cursor: 'move',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Tooltip title={t('Exit')}>
                        <IconButton onClick={onClose}>
                            <CancelIcon style={{ color: '#304FFF' }} />
                        </IconButton>
                    </Tooltip>
                    <div className="edit-title">{t('AddClientItem')}</div>
                    <div />
                </div>

                {/* Items */}
                {itemsToAdd.map((item, index) => (
                    <div key={index} style={{ marginBottom: 24 }}>
                        {index > 0 && (
                            <hr
                                style={{
                                    border: '0.5px solid #ddd',
                                    marginBottom: 24,
                                }}
                            />
                        )}

                        <div className="form-row" style={{ marginBottom: 16 }}>
                            <div className="edit-form-row-item" style={{ marginBottom: 8 }}>
                                <TextField
                                    fullWidth
                                    placeholder={t('Makat')}
                                    value={item.makat}
                                    onChange={(e) => handleChange(index, 'makat', e.target.value)}
                                    variant="standard"
                                    InputProps={inputPropsStyle}
                                    inputProps={inputStyle}
                                    required
                                    error={!item.makat || item.makat.trim() === ''}
                                    helperText={(!item.makat || item.makat.trim() === '') ? t('Required') : ''}
                                />
                            </div>
                        </div>

                        <div className="form-row" style={{ marginBottom: 16 }}>
                            <div className="edit-form-row-item" style={{ marginBottom: 8 }}>
                                <TextField
                                    fullWidth
                                    placeholder={t('Name')}
                                    value={item.name}
                                    onChange={(e) => handleChange(index, 'name', e.target.value)}
                                    variant="standard"
                                    InputProps={inputPropsStyle}
                                    inputProps={inputStyle}
                                    required
                                    error={!item.name || item.name.trim() === ''}
                                    helperText={(!item.name || item.name.trim() === '') ? t('Required') : ''}
                                />
                            </div>
                        </div>

                        <div className="form-row" style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            <div className="edit-form-row-item" style={{ flex: 1 }}>
                                <ClientItemPricesEditor
                                    value={item.prices}
                                    onChange={(prices) => handleChange(index, 'prices', prices)}
                                    direction={direction}
                                    inputPropsStyle={inputPropsStyle}
                                    inputStyle={inputStyle}
                                />
                            </div>
                            <div className="edit-form-row-item" style={{ marginLeft: 8, minWidth: 140, marginTop: 28 }}>
                                <FormControl variant="standard" fullWidth>
                                    <InputLabel id={`vat-label-${index}`}>{t('Vat')}</InputLabel>
                                    <Select
                                        labelId={`vat-label-${index}`}
                                        value={item.includeVat ? 'ItemWithVat' : 'ItemWithoutVat'}
                                        onChange={(e) => handleChange(index, 'includeVat', e.target.value === 'ItemWithVat')}
                                        label={t('Vat')}
                                        MenuProps={{
                                            sx: { zIndex: 9999 },
                                            PaperProps: { sx: { zIndex: 9999 } },
                                        }}
                                        sx={{
                                            height: '48px',
                                            padding: '0 10px',
                                            borderRadius: '8px',
                                            background: '#F5F5F6',
                                            border: '1px solid transparent',
                                            '&:focus-within': {
                                                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                                            },
                                            '& .MuiSelect-select': {
                                                padding: '6px 8px',
                                                textAlign: direction === 'rtl' ? 'left' : 'right',
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: '#666',
                                                fontSize: '14px',
                                            },
                                        }}
                                    >
                                        <MenuItem dir={direction} value="ItemWithVat">{t('ItemWithVat')}</MenuItem>
                                        <MenuItem dir={direction} value="ItemWithoutVat">{t('ItemWithoutVat')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                            {itemsToAdd.length > 1 && (
                                <Tooltip title={t('RemoveItem')}>
                                    <IconButton onClick={() => handleRemoveRow(index)} sx={{ mt: 3 }}>
                                        <DeleteIcon style={{ color: 'red' }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>

                        {/* Image Upload */}
                        <div className="form-row" style={{ marginBottom: 16 }}>
                            <div className="edit-form-row-item-title" style={{ marginBottom: 8 }}>
                                {t('ItemImage')}
                            </div>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {item.imagePreview && (
                                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                        <Avatar
                                            src={item.imagePreview}
                                            variant="rounded"
                                            sx={{ width: 80, height: 80 }}
                                        />
                                        <Tooltip title={t('RemoveImage')}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemoveImage(index)}
                                                sx={{
                                                    position: 'absolute',
                                                    top: -8,
                                                    right: -8,
                                                    width: 24,
                                                    height: 24,
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #ccc',
                                                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                                                    '&:hover': {
                                                        backgroundColor: '#fee',
                                                    },
                                                }}
                                            >
                                                <CloseIcon sx={{ fontSize: 16, color: '#d32f2f' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )}
                                <input
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    id={`image-upload-${index}`}
                                    type="file"
                                    onChange={(e) => handleImageChange(index, e)}
                                />
                                <label htmlFor={`image-upload-${index}`}>
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<PhotoCamera />}
                                        sx={{
                                            borderColor: '#304FFF',
                                            color: '#304FFF',
                                            '&:hover': {
                                                borderColor: '#1E40FF',
                                                backgroundColor: 'rgba(48, 79, 255, 0.04)',
                                            },
                                        }}
                                    >
                                        {t('UploadImage')}
                                    </Button>
                                </label>
                            </Box>
                        </div>
                    </div>
                ))}

                {/* Add Item Button */}
                <div
                    onClick={handleAddRow}
                    className="edit-add-button"
                    style={{
                        alignSelf: direction === 'rtl' ? 'flex-start' : 'flex-end',
                        marginBottom: 16,
                        cursor: 'pointer',
                        padding: '6px 16px',
                        backgroundColor: '#304FFF',
                        color: '#fff',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 500,
                        width: 'fit-content',
                    }}
                >
                    {t('AddItem')}
                </div>

                {/* Footer Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '16px', gap: '10px' }}>
                    <div onClick={onClose} className="edit-cancel-button">
                        {t('Cancel')}
                    </div>
                    <div 
                        onClick={handleSave} 
                        className={`edit-save-button ${!isFormValid() ? 'disabled' : ''}`}
                        style={{
                            opacity: !isFormValid() ? 0.5 : 1,
                            cursor: !isFormValid() ? 'not-allowed' : 'pointer',
                            backgroundColor: !isFormValid() ? '#ccc' : undefined
                        }}
                    >
                        {t('Save')}
                    </div>
                </div>
            </StyledContainer>
        </Draggable>
    );
}

export default ClientItemCreateModal;
